import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Phone, Mail, Building, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from './Button';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  contact: string;
  contactType: 'phone' | 'email';
  company: string;
  solution: string;
  description: string;
}

/**
 * 免费咨询弹窗组件
 * 包含联系人信息收集表单和提交成功反馈
 */
export const ConsultationModal: React.FC<ConsultationModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    contact: '',
    contactType: 'phone',
    company: '',
    solution: '',
    description: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  // 解决方案选项
  const solutionOptions = [
    { value: '', label: '请选择您感兴趣的解决方案' },
    { value: 'charging', label: '充电服务解决方案' },
    { value: 'refueling', label: '加油服务解决方案' },
    { value: 'maintenance', label: '维修保养解决方案' },
    { value: 'retail', label: '便利店零售解决方案' },
    { value: 'rental', label: '租车服务解决方案' },
    { value: 'custom', label: '定制化解决方案' }
  ];

  /**
   * 表单验证函数
   */
  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = '请输入联系人姓名';
    }
    
    if (!formData.contact.trim()) {
      newErrors.contact = '请输入联系方式';
    } else {
      if (formData.contactType === 'phone') {
        const phoneRegex = /^1[3-9]\d{9}$/;
        if (!phoneRegex.test(formData.contact)) {
          newErrors.contact = '请输入正确的手机号码';
        }
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.contact)) {
          newErrors.contact = '请输入正确的邮箱地址';
        }
      }
    }
    
    if (!formData.company.trim()) {
      newErrors.company = '请输入公司名称';
    }
    
    if (!formData.solution) {
      newErrors.solution = '请选择解决方案';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * 处理表单提交
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // 提交成功
      setIsSubmitted(true);
      
      // 3秒后自动关闭弹窗
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      console.error('提交失败:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 处理输入变化
   */
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 清除对应字段的错误
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  /**
   * 关闭弹窗并重置状态
   */
  const handleClose = () => {
    setIsSubmitted(false);
    setFormData({
      name: '',
      contact: '',
      contactType: 'phone',
      company: '',
      solution: '',
      description: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 成功提示页面 */}
            {isSubmitted ? (
              <div className="p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center"
                >
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </motion.div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">提交成功！</h3>
                <p className="text-gray-600 leading-relaxed">
                  我们已经收到您的信息，将会尽快与您取得联系，感谢您的支持！
                </p>
              </div>
            ) : (
              <>
                {/* 表单头部 */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900">免费咨询</h2>
                  <button
                    onClick={handleClose}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* 表单内容 */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  {/* 联系人姓名 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      联系人姓名 *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="请输入您的姓名"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  {/* 联系方式 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {formData.contactType === 'phone' ? (
                        <Phone className="w-4 h-4 inline mr-2" />
                      ) : (
                        <Mail className="w-4 h-4 inline mr-2" />
                      )}
                      联系方式 *
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.contactType}
                        onChange={(e) => handleInputChange('contactType', e.target.value)}
                        className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="phone">手机</option>
                        <option value="email">邮箱</option>
                      </select>
                      <input
                        type={formData.contactType === 'phone' ? 'tel' : 'email'}
                        value={formData.contact}
                        onChange={(e) => handleInputChange('contact', e.target.value)}
                        className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.contact ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={formData.contactType === 'phone' ? '请输入手机号码' : '请输入邮箱地址'}
                      />
                    </div>
                    {errors.contact && (
                      <p className="mt-1 text-sm text-red-600">{errors.contact}</p>
                    )}
                  </div>

                  {/* 公司名称 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Building className="w-4 h-4 inline mr-2" />
                      公司名称 *
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        errors.company ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="请输入公司名称"
                    />
                    {errors.company && (
                      <p className="mt-1 text-sm text-red-600">{errors.company}</p>
                    )}
                  </div>

                  {/* 解决方案选择 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      您希望使用的解决方案 *
                    </label>
                    <select
                      value={formData.solution}
                      onChange={(e) => handleInputChange('solution', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        errors.solution ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      {solutionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.solution && (
                      <p className="mt-1 text-sm text-red-600">{errors.solution}</p>
                    )}
                  </div>

                  {/* 具体需求描述 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MessageSquare className="w-4 h-4 inline mr-2" />
                      具体需求描述
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all resize-none"
                      style={{}}
                      onFocus={(e) => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(22, 119, 255, 0.2)'}
                      onBlur={(e) => e.currentTarget.style.boxShadow = 'none'}
                      placeholder="请详细描述您的需求，我们将为您提供更精准的解决方案"
                    />
                  </div>

                  {/* 提交按钮 */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleClose}
                      className="flex-1"
                    >
                      取消
                    </Button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{backgroundColor: '#1677FF'}}
                      className="flex-1 hover:opacity-90 transition-all duration-300 px-4 py-3 text-white font-medium rounded-lg disabled:opacity-50"
                    >
                      {isSubmitting ? '提交中...' : '提交咨询'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};